<template>
  <div class="page">
    <div class="card">
      <div class="card-header">
        <i class="ti ti-users"></i>
        <h2>Usuários cadastrados no sistema</h2>
      </div>

      <div class="toolbar">
        <div class="search-wrap">
          <i class="ti ti-search"></i>
          <input v-model="searchQuery" name="pesquisa-lista-usuarios" autocomplete="off" type="text" placeholder="Pesquisar por nome ou e-mail..." />
        </div>
      </div>

      <ul class="user-list">
        <Carregamento v-if="carregando" />

        <div v-else-if="usuarios && usuarios.length > 0">
          <li v-for="usuario in usuarios" :key="usuario.id" class="list-item" @click="selecionarUsuario(usuario)">
            <div class="avatar">{{ iniciais(usuario.nome) }}</div>
            <div class="item-info">
              <span class="item-name">{{ usuario.nome }}</span>
              <span class="item-email">{{ usuario.email }}</span>
            </div>
            <span class="badge">{{ usuario.tipo }}</span>
          </li>
        </div>

        <div v-else class="empty">
          <i class="ti ti-user-x"></i>
          <span>Nenhum usuário encontrado.</span>
        </div>
      </ul>
      <Paginacao v-if="totalPaginas > 1" :pagina-atual="paginaAtual" :total-paginas="totalPaginas"
        @mudar-pagina="mudarPagina" />
    </div>
  </div>
  <RouterView />
  <FormEditarPerfil v-if="route.query.modal === 'editar-perfil'" :dadosUsuario="usuarioSelecionado"
    @fecharModal="fecharModal" />
</template>

<script setup>
import FormEditarPerfil from "@/components/usuarios/FormEditarPerfil.vue";
import Carregamento from "@/components/common/Carregamento.vue";
import router from "@/router";
import usuariosService from "@/service/usuariosService";
import { onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import Paginacao from "@/components/common/paginacaoComponent.vue";

const carregando = ref(false);
const editarInformacoes = ref(false);
const searchQuery = ref("");
const usuarios = ref([]);
const usuarioSelecionado = ref({});
const route = useRoute();
const paginaAtual = ref(0);
const totalPaginas = ref(0);
const tamanhoPagina = 30;

function iniciais(nome = "") {
  if (!nome) return "U";
  const partes = nome.trim().split(" ").filter(Boolean);
  return (partes[0][0] + (partes[1] ? partes[1][0] : "")).toUpperCase();
}

async function carregarUsuarios(pagina = 0) {
  carregando.value = true;

  try {
    const termo = searchQuery.value?.trim();
    let response;

    if (termo) {
      if (termo.includes("@")) {
        // Busca por e-mail no backend com fallback para termo/nome
        response = await usuariosService.pesquisarUsuarios(
          pagina,
          tamanhoPagina,
          "",
          "",
          termo,
          termo
        );
        if (!response?.content?.length) {
          const fallback = await usuariosService.pesquisarUsuarios(
            pagina,
            tamanhoPagina,
            termo,
            "",
            "",
            termo
          );
          if (fallback?.content?.length) response = fallback;
        }
      } else {
        // Busca por nome ou termo geral com fallback para busca por e-mail
        response = await usuariosService.pesquisarUsuarios(
          pagina,
          tamanhoPagina,
          termo,
          "",
          "",
          termo
        );
        if (!response?.content?.length) {
          const fallback = await usuariosService.pesquisarUsuarios(
            pagina,
            tamanhoPagina,
            "",
            "",
            termo,
            termo
          );
          if (fallback?.content?.length) response = fallback;
        }
      }
    } else {
      response = await usuariosService.getAllUsuarios(pagina, tamanhoPagina);
    }

    usuarios.value =
      response?.content ?? (Array.isArray(response) ? response : []);
    totalPaginas.value =
      response?.totalPages ?? (usuarios.value.length > 0 ? 1 : 0);
    paginaAtual.value = response?.number ?? pagina;
  } catch (error) {
    console.error("Erro ao carregar usuários:", error);
    usuarios.value = [];
    totalPaginas.value = 0;
  } finally {
    carregando.value = false;
  }
}

// Agora só empurra a página pra URL — quem dispara o carregamento é o watch
function mudarPagina(pagina) {
  router.push({
    query: {
      ...route.query,
      page: pagina,
    },
  });
}

let debounceTimer = null;
watch(searchQuery, () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    paginaAtual.value = 0;
    if (route.query.page && route.query.page !== "0") {
      router.push({
        query: {
          ...route.query,
          page: 0,
        },
      });
    }
    carregarUsuarios(0);
  }, 350);
});

function selecionarUsuario(usuario) {
  usuarioSelecionado.value = usuario;
  editarInformacoes.value = true;

  router.push({
    query: {
      ...route.query, // <-- importante: preserva o "page" que já estava na URL
      modal: "editar-perfil",
    },
  });
}

async function fecharModal() {
  const { modal, ...resto } = route.query;
  router.push({ query: resto }); // remove "modal" da URL, mantém "page"
  await carregarUsuarios(paginaAtual.value);
}

// Reage a qualquer troca de página pela URL: clique nos botões,
// botão voltar/avançar do navegador, ou link compartilhado com ?page=5
watch(
  () => route.query.page,
  (novaPagina) => {
    const pagina = Number(novaPagina ?? 0);
    if (pagina !== paginaAtual.value) {
      carregarUsuarios(pagina);
    }
  }
);

onMounted(async () => {
  const paginaInicial = Number(route.query.page ?? 0);
  await carregarUsuarios(paginaInicial);
});
</script>

<style scoped>
@import url("https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/tabler-icons.min.css");

*,
*::before,
*::after {
  box-sizing: border-box;
}

.page {
  padding: 1.5rem;
  min-height: 100vh;
}

.card {
  background: var(--branco);
  border: 0.5px solid hsla(0, 0%, 0%, 0.12);
  border-radius: 12px;
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 1.25rem 1.5rem;
  border-bottom: 0.5px solid hsla(0, 0%, 0%, 0.1);
}

.card-header i {
  font-size: 20px;
  color: var(--cinza-medio);
}

.card-header h2 {
  font-size: 16px;
  font-weight: 500;
  color: var(--preto);
  margin: 0;
}

.toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 0.5px solid hsla(0, 0%, 0%, 0.1);
  background: var(--roxo-superficie);
}

.search-wrap {
  flex: 1;
  position: relative;
}

.search-wrap i {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  color: var(--cinza-medio);
}

.search-wrap input {
  width: 100%;
  padding: 7px 12px 7px 34px;
  border: 0.5px solid hsla(0, 0%, 0%, 0.2);
  border-radius: 8px;
  font-size: 14px;
  color: var(--preto);
  background: var(--branco);
  outline: none;
  transition: border-color 0.15s;
}

.search-wrap input:focus {
  border-color: var(--cinza-medio);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 0.5px solid hsla(0, 0%, 0%, 0.2);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  background: var(--branco);
  color: var(--cinza-escuro);
  transition: background 0.15s;
  white-space: nowrap;
}

.btn:hover {
  background: var(--cinza-claro);
}

.btn i {
  font-size: 15px;
}

.btn-active {
  background: var(--cinza-claro);
  color: var(--cinza-escuro);
  border-color: var(--cinza-claro);
}

.filters-panel {
  display: none;
  padding: 1rem 1.5rem;
  border-bottom: 0.5px solid hsla(0, 0%, 0%, 0.1);
  background: var(--branco);
}

.filters-panel.open {
  display: block;
}

.filters-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--cinza-medio);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 10px;
}

.filters-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.chip {
  padding: 5px 14px;
  border: 0.5px solid hsla(0, 0%, 0%, 0.18);
  border-radius: 999px;
  font-size: 13px;
  cursor: pointer;
  background: var(--cinza-claro);
  color: var(--cinza-escuro);
  transition: all 0.15s;
  font-family: inherit;
}

.chip.selected {
  background: var(--cinza-claro);
  color: var(--cinza-escuro);
  border-color: var(--cinza-claro);
  font-weight: 500;
}

.filters-apply {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
}

.user-list {
  list-style: none;
  cursor: pointer;
  margin: 0;
  padding: 0;
}

.list-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 1.5rem;
  border-bottom: 0.5px solid hsla(0, 0%, 0%, 0.08);
  transition: background 0.1s;
}

.list-item:last-child {
  border-bottom: none;
}

.list-item:hover {
  background: var(--roxo-superficie);
}

.avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--cinza-claro);
  color: var(--cinza-escuro);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
}

.item-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.item-name {
  font-size: 14px;
  color: var(--preto);
}

.item-email {
  font-size: 11px;
  color: var(--cinza-medio);
}

.badge {
  margin-left: auto;
  font-size: 11px;
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--cinza-claro);
  color: var(--cinza-escuro);
  border: 0.5px solid hsla(0, 0%, 0%, 0.1);
}

.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 2.5rem;
  color: var(--cinza-medio);
  font-size: 14px;
}

.empty i {
  font-size: 24px;
}
</style>
